import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Trophy } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";
import { DTMBarChart } from "@/components/charts/DTMBarChart";
import { DTMComparisonBarChart } from "@/components/charts/DTMComparisonBarChart";
import { ToolCTA, InlineNewsletter, ShareBar, RelatedResearch } from "@/components/InlineCTA";

/* ── Chart Data ─────────────────────────────────────────── */

const CHART_A_DATA = [
  { label: "RB", seriesA: 46.1, seriesB: 78.4 },
  { label: "WR", seriesA: 56.2, seriesB: 72.6 },
  { label: "QB", seriesA: 40.3, seriesB: 70.2 },
  { label: "TE", seriesA: 41.3, seriesB: 67.2 },
];

const CHART_B_DATA = [
  { label: "WR", value: 35.7, color: "#45b7d1" },
  { label: "RB", value: 30.1, color: "#4ecdc4" },
  { label: "QB", value: 22.5, color: "#ff6b6b" },
  { label: "TE", value: 11.7, color: "#f9ca24" },
];

const CHART_C_DATA = [
  { label: "QB1–3", value: 28.4, highlight: true },
  { label: "QB4–6", value: 19.5 },
  { label: "QB7–12", value: 27.8 },
  { label: "QB13–24", value: 22.8 },
  { label: "QB25+", value: 1.5 },
];

const DNA_LEADERBOARD = [
  { player: "Bijan Robinson", pos: "RB", rate: "23.0%" },
  { player: "Puka Nacua", pos: "WR", rate: "19.3%" },
  { player: "Chase Brown", pos: "RB", rate: "17.2%" },
  { player: "Derrick Henry", pos: "RB", rate: "15.1%" },
  { player: "Christian McCaffrey", pos: "RB", rate: "14.6%" },
  { player: "De\u2019Von Achane", pos: "RB", rate: "14.5%" },
  { player: "Jahmyr Gibbs", pos: "RB", rate: "14.4%" },
  { player: "Trey McBride", pos: "TE", rate: "14.3%" },
  { player: "Jonathan Taylor", pos: "RB", rate: "11.9%" },
  { player: "Ja\u2019Marr Chase", pos: "WR", rate: "11.3%" },
  { player: "Brock Purdy", pos: "QB", rate: "11.3%" },
  { player: "Chris Olave", pos: "WR", rate: "11.2%" },
  { player: "George Kittle", pos: "TE", rate: "12.1%" },
  { player: "Saquon Barkley", pos: "RB", rate: "10.1%" },
  { player: "Baker Mayfield", pos: "QB", rate: "10.1%" },
];

const POS_COLORS = { QB: "#ff6b6b", RB: "#4ecdc4", WR: "#45b7d1", TE: "#f9ca24" };

/* ── FAQ Schema ─────────────────────────────────────────── */

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What do winning dynasty rosters have in common?",
      acceptedAnswer: { "@type": "Answer", text: "A core of elite skill players (78.4% had a top-10 RB, 72.6% a top-10 WR), concentrated value (the top 3 players held about 38% of roster value), and a productive rather than young age profile (average 27.5 years old). Champions bet on a few difference-makers instead of spreading value across many solid players." },
    },
    {
      "@type": "Question",
      name: "Do you need an elite quarterback to win a dynasty title?",
      acceptedAnswer: { "@type": "Answer", text: "No. Only 28.4% of champions had a top-3 QB, and more than 70% won without one. Plenty won with a mid-range starter. Elite QB helps, especially in Superflex, but it isn't required. Punting the position entirely is the real mistake." },
    },
    {
      "@type": "Question",
      name: "How old is the average championship roster?",
      acceptedAnswer: { "@type": "Answer", text: "About 27.5 years old. Only 7.3% of champions were genuinely young rosters. Winning teams valued proven production over youth, which pushes back on the common instinct to get younger at all costs." },
    },
    {
      "@type": "Question",
      name: "Should I trade depth for a star?",
      acceptedAnswer: { "@type": "Answer", text: "The data supports it. Champions concentrated nearly 40% of their value in their top 3 players. A concentrated core of elite talent consistently beat broad, even rosters, even though those consolidation trades often look even on a calculator." },
    },
    {
      "@type": "Question",
      name: "Which players show up on the most championship rosters?",
      acceptedAnswer: { "@type": "Answer", text: "Workhorse running backs and elite young receivers lead the way — players who deliver real league-winning production. Targeting genuine difference-makers over famous-but-fading names is its own edge." },
    },
    {
      "@type": "Question",
      name: "Should dynasty drafts be linear or snake?",
      acceptedAnswer: { "@type": "Answer", text: "Rookie drafts are usually linear (the same order every round), which rewards the weakest teams with the best pick at every turn and helps rebuilders restock. Startup drafts are typically snake to keep the initial talent distribution fair. So it's common to run a snake startup and linear rookie drafts in the same league." },
    },
    {
      "@type": "Question",
      name: "How many titles make a dynasty?",
      acceptedAnswer: { "@type": "Answer", text: "There's no official number, but repeat winning is rarer than people think. In our data, only 15.6% of champions ever won multiple titles and just 11.6% went back-to-back. So even two championships with the same core puts you in a small, genuinely dominant group." },
    },
  ],
};

/* ── Stat Card ──────────────────────────────────────────── */

function StatCard({ label, value }) {
  return (
    <div className="border border-[#262626] bg-[#171717] p-3 text-center" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="font-mono text-xl font-bold text-white">{value}</p>
      <p className="text-[9px] font-bold uppercase text-[#555] mt-1">{label}</p>
    </div>
  );
}

/* ── Main Component ─────────────────────────────────────── */

export default function ChampionshipRostersResearch() {
  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="championship-rosters-research">
      <SEO
        title="What Championship Rosters Have in Common (10,000 Real Teams) — Dynasty Trade Maker"
        description="We analyzed 9,983 real championship rosters. 78.4% had a top-10 RB. Top 3 players held 38% of value. Average age: 27.5. Here's the real profile of a dynasty champion."
        path="/research/what-championship-rosters-have-in-common"
        image="https://dynastytrademaker.com/hero-images/what-championship-rosters-have-in-common.jpg"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>
      <PublicNav />

      <article className="max-w-3xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <Link to="/research" className="text-[10px] text-[#555] hover:text-[#a3a3a3] no-underline uppercase tracking-wider">Research</Link>

        {/* Header */}
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mt-4 mb-2">9,983 CHAMPIONSHIP ROSTERS ANALYZED</span>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-4" data-testid="article-title">

        {/* Hero Image */}
        <div className="my-6 border border-[#262626] rounded-sm overflow-hidden" data-testid="hero-image">
          <img src="/hero-images/what-championship-rosters-have-in-common.jpg" alt="" className="w-full h-auto" loading="eager" />
        </div>
        <ShareBar title="What Championship Rosters Have in Common" path="/research/what-championship-rosters-have-in-common" />
          WHAT CHAMPIONSHIP ROSTERS <span className="text-[#ccff00]">HAVE IN COMMON</span>
        </h1>
        <p className="text-base text-[#a3a3a3] mb-10">
          {"We pulled 9,983 real championship rosters from completed Sleeper dynasty leagues and asked a simple question: what do these teams actually have in common? Not what should win. What did win. Here\u2019s the real profile of a champion."}
        </p>


        <div className="space-y-10 text-sm text-[#a3a3a3] leading-relaxed">

          {/* ── Elite Skill Players ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">{"CHAMPIONS ARE BUILT ON ELITE SKILL PLAYERS"}</h2>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              <StatCard label="Champions" value="9,983" />
              <StatCard label="Had Top-10 RB" value="78.4%" />
              <StatCard label="Had Top-10 WR" value="72.6%" />
              <StatCard label="Had Top-10 QB" value="70.2%" />
            </div>

            <p className="mb-4">
              {"The clearest pattern in the whole dataset: winning teams had elite talent at the skill positions. Of the 9,983 champions, "}
              <strong className="text-white">78.4%</strong>
              {" rostered a top-10 running back and "}
              <strong className="text-white">72.6%</strong>
              {" rostered a top-10 receiver. 56.2% had a top-5 receiver and 46.1% had a top-5 running back. These teams didn\u2019t back into titles with solid contributors. They had genuine difference-makers."}
            </p>

            {/* CHART A */}
            <DTMComparisonBarChart
              data={CHART_A_DATA}
              title="Positional Championship Rates"
              subtitle="% of 9,983 champions with a top-5 or top-10 player at each position"
              source="DTM Master Data Export \u2014 B1.csv | 9,983 championship rosters"
              seriesAKey="seriesA"
              seriesBKey="seriesB"
              seriesAName="Top-5"
              seriesBName="Top-10"
              seriesAColor="#ccff00"
              seriesBColor="#555"
              height={240}
              formatter={(v) => `${v}%`}
              domain={[0, 85]}
            />

            <p className="mt-5">
              {"The takeaway for your own team is direct: elite skill players are not a luxury, they\u2019re the foundation. If your roster is a wide, flat collection of decent starters with no true studs, the data says you\u2019re built to finish second."}
            </p>
          </section>

          {/* ── Value Concentration ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">{"THEY CONCENTRATE VALUE, THEY DON\u2019T SPREAD IT"}</h2>
            <p className="mb-4">
              {"On the average championship roster, the top 3 players held "}
              <strong className="text-[#ccff00]">38.3%</strong>
              {" of the entire roster\u2019s total value. Three players, nearly 40% of everything. These teams made a clear bet on a handful of studs and filled in around them."}
            </p>
            <div className="border border-[#262626] bg-[#171717] p-5 text-center mb-4">
              <p className="font-mono text-4xl font-bold text-[#ccff00]">38.3%</p>
              <p className="text-[10px] text-[#555] uppercase mt-1">{"of champion roster value held by top 3 players"}</p>
            </div>
            <p>
              {"That reframes the classic \u201Ctwo good players for one great one\u201D trade. It looks even on a calculator, and managers agonize over giving up depth. But the champions consistently made exactly that move. A concentrated core of elite talent beats a broad roster of good-not-great players."}
            </p>
          </section>

          {/* ── Age ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">THE REAL AGE OF A CHAMPION</h2>
            <p className="mb-4">
              {"Dynasty culture is obsessed with getting younger. The winning teams didn\u2019t play that way."}
            </p>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="border border-[#262626] bg-[#171717] p-3 text-center">
                <p className="font-mono text-2xl font-bold text-white">27.55</p>
                <p className="text-[9px] text-[#555] uppercase mt-1">Avg Age</p>
              </div>
              <div className="border border-[#262626] bg-[#171717] p-3 text-center">
                <p className="font-mono text-2xl font-bold text-white">7.3%</p>
                <p className="text-[9px] text-[#555] uppercase mt-1">{"Young (<26)"}</p>
              </div>
              <div className="border border-[#262626] bg-[#171717] p-3 text-center">
                <p className="font-mono text-2xl font-bold text-white">33.3%</p>
                <p className="text-[9px] text-[#555] uppercase mt-1">{"Older (28+)"}</p>
              </div>
            </div>
            <p>
              {"That doesn\u2019t mean age is irrelevant. It means age is a tool, not a rule. The champions drafted and traded for players who were good "}
              <em>during the season they won</em>
              {", not just players who\u2019d be good in three years. If you\u2019re a contender selling a productive 28-year-old for pennies to \u201Cget younger,\u201D the data says you may be handing your title to someone else."}
            </p>
          </section>

          <ToolCTA
            text="See how your roster compares to 9,983 champions"
            subtext="Run your league through the Championship Blueprint for a full breakdown."
            buttonText="GRADE YOUR ROSTER"
            buttonLink="/championship-blueprint"
            icon={<Trophy size={12} weight="fill" />}
          />

          {/* ── Positional Allocation ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">WHERE CHAMPIONS PUT THEIR VALUE</h2>
            <p className="mb-5">
              {"Zoom out from individual players and a clear positional shape emerges. Receivers led the way at "}
              <strong className="text-white">35.7%</strong>
              {" of roster value, followed by running back at "}
              <strong className="text-white">30.1%</strong>
              {", quarterback at "}
              <strong className="text-white">22.5%</strong>
              {", and tight end at "}
              <strong className="text-white">11.7%</strong>
              {". Receivers and backs together made up roughly two-thirds of a champion\u2019s value."}
            </p>

            {/* CHART B */}
            <DTMBarChart
              data={CHART_B_DATA}
              title="Championship Roster Value by Position"
              subtitle="Share of total roster value on champion teams"
              source="DTM Master Data Export \u2014 B4.csv | 9,983 championship rosters"
              layout="vertical"
              height={180}
              formatter={(v) => `${v}%`}
              domain={[0, 40]}
              showLabels={true}
            />

            <p className="mt-5">
              {"Treat that as a compass, not a mandate. But if your team looks wildly different \u2014 say, half your value tied up in quarterbacks, or almost nothing at receiver \u2014 it\u2019s worth asking whether you\u2019ve drifted from what winning rosters actually look like."}
            </p>
          </section>

          {/* ── QB Tiers ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">{"DO YOU NEED THE ELITE QUARTERBACK?"}</h2>
            <p className="mb-5">
              {"Yes, having an elite quarterback helps: "}
              <strong className="text-white">28.4%</strong>
              {" of champions rostered a top-3 QB. But another 27.8% won with a QB7\u201312, a clear mid-range starter, and 22.8% won with a QB outside the top 12 entirely."}
            </p>

            {/* CHART C */}
            <DTMBarChart
              data={CHART_C_DATA}
              title="QB Tier vs. Championship Share"
              subtitle="% of 9,983 champions by their best QB tier"
              source="DTM Master Data Export \u2014 B5.csv"
              layout="horizontal"
              height={240}
              formatter={(v) => `${v}%`}
              domain={[0, 32]}
              defaultColor="#333"
            />

            <p className="mt-5">
              {"More than seven in ten champions won "}
              <em>without</em>
              {" a top-3 QB. You don\u2019t have to win the quarterback lottery to win a title; you have to not punt the position entirely. (This shifts in Superflex, where you need two starters, but even there elite is an edge, not a prerequisite.)"}
            </p>
          </section>

          <InlineNewsletter context="championship" source="championship-rosters-research" />

          {/* ── Championship DNA ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">THE PLAYERS WHO SHOW UP ON <span className="text-[#ccff00]">CHAMPIONSHIP TEAMS</span></h2>
            <p className="mb-5">
              {"Some players just appear on winning rosters over and over. We ranked every player by how often they showed up on a champion, indexed against overall rostership, to find the genuine championship-DNA players."}
            </p>

            {/* CHART D — Table */}
            <div className="border border-[#262626] overflow-hidden mb-4">
              <div className="px-5 pt-4 pb-2">
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#ccff00] mb-0.5">Championship DNA Leaderboard</h3>
                <p className="text-[10px] text-[#555]">Top 15 players by championship roster appearance rate</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="dna-leaderboard">
                  <thead>
                    <tr className="border-b border-[#262626] text-[9px] font-bold uppercase text-[#555]">
                      <th className="text-left px-4 py-2">#</th>
                      <th className="text-left px-4 py-2">Player</th>
                      <th className="text-left px-4 py-2">Pos</th>
                      <th className="text-right px-4 py-2">Champ Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DNA_LEADERBOARD.map((p, i) => (
                      <tr key={p.player} className="border-b border-[#1a1a1a] hover:bg-[#1a1a1a]/50 transition-colors">
                        <td className="px-4 py-2 font-mono text-[#555] text-xs">{i + 1}</td>
                        <td className="px-4 py-2 text-white font-semibold text-xs">{p.player}</td>
                        <td className="px-4 py-2">
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm" style={{ color: POS_COLORS[p.pos], backgroundColor: `${POS_COLORS[p.pos]}15` }}>{p.pos}</span>
                        </td>
                        <td className="px-4 py-2 text-right font-mono text-[#ccff00] text-xs">{p.rate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-5 pb-3 text-[10px] text-[#444]">
                {"Source: DTM Master Data Export \u2014 B6.csv | Indexed against total rostership"}
              </div>
            </div>

            <p>
              {"The leaders won\u2019t shock you, but the pattern is instructive. Bijan Robinson topped the list at 23.0%. What ties them together: they\u2019re players who deliver league-winning production, not just name value."}
            </p>
          </section>

          {/* ── What This Means ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">WHAT THIS MEANS FOR YOUR TEAM</h2>
            <div className="border border-[#262626] bg-[#171717] p-5 space-y-3 mb-4">
              <div className="flex items-start gap-3">
                <span className="text-[#ccff00] font-bold text-xs shrink-0 mt-0.5">01</span>
                <span className="text-sm text-[#d4d4d4]"><strong className="text-white">A core of elite skill players.</strong>{" Most champions had a top-10 RB and a top-10 WR. Difference-makers, not depth."}</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[#ccff00] font-bold text-xs shrink-0 mt-0.5">02</span>
                <span className="text-sm text-[#d4d4d4]"><strong className="text-white">Concentrated value.</strong>{" The top 3 players held nearly 40% of roster value. Champions bet big on a few, not evenly on many."}</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[#ccff00] font-bold text-xs shrink-0 mt-0.5">03</span>
                <span className="text-sm text-[#d4d4d4]"><strong className="text-white">Production over youth.</strong>{" The average champion was 27.5 years old. Winning rosters valued proven players, not just young ones."}</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[#ccff00] font-bold text-xs shrink-0 mt-0.5">04</span>
                <span className="text-sm text-[#d4d4d4]"><strong className="text-white">A receiver-and-back foundation.</strong>{" Two-thirds of champion value sat at WR and RB."}</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[#ccff00] font-bold text-xs shrink-0 mt-0.5">05</span>
                <span className="text-sm text-[#d4d4d4]"><strong className="text-white">A quarterback, not necessarily an elite one.</strong>{" Most champions won without a top-3 QB, but almost none punted the position."}</span>
              </div>
            </div>
            <p>
              {"None of this is a guarantee \u2014 dynasty has too much luck for guarantees. But it\u2019s the real shape of a winner, drawn from the teams that actually got there. Build toward that profile and you\u2019re playing the odds the champions played."}
            </p>
          </section>

          {/* ── FAQ ────────────────── */}
          <section className="mt-12 border-t border-[#262626] pt-8">
            <h2 className="font-heading text-2xl text-white uppercase mb-5">FREQUENTLY ASKED QUESTIONS</h2>
            <div className="space-y-5">
              {FAQ_SCHEMA.mainEntity.map((q, i) => (
                <div key={i} className="border border-[#262626] bg-[#171717] p-4 rounded-sm">
                  <h3 className="text-sm font-bold text-white mb-2">{q.name}</h3>
                  <p className="text-sm text-[#a3a3a3] leading-relaxed">{q.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* ── Share + Bottom CTA ────────────────── */}
        <div className="mt-10 mb-6">
          <ShareBar title="What Championship Rosters Have in Common" path="/research/what-championship-rosters-have-in-common" />
        </div>
        <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-6 text-center">
          <h3 className="font-heading text-lg text-white uppercase mb-2">GRADE YOUR ROSTER VS 9,983 CHAMPIONS</h3>
          <p className="text-xs text-[#888] mb-4">{"See where you match the winners and where you fall short."}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/championship-blueprint" className="h-9 px-5 bg-[#ccff00] text-black rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold hover:bg-[#b8e600] transition-colors" data-testid="cta-championship-blueprint">
              <Trophy size={12} weight="fill" /> CHAMPIONSHIP BLUEPRINT
            </Link>
            <Link to="/trade-calculator" className="text-[11px] text-[#ccff00] no-underline hover:underline flex items-center gap-1" data-testid="cta-trade-calculator">{"Value a Trade, Free"} <ArrowRight size={11} /></Link>
          </div>
        </div>

        {/* ── Footnote ────────────────── */}
        <RelatedResearch slug="what-championship-rosters-have-in-common" />
        <p className="mt-6 text-center text-[10px] text-[#444] italic">
          {"Based on 9,983 championship rosters from 6,976 Sleeper dynasty leagues, 2021\u20132026. Player values use current FantasyCalc values."}
        </p>

        {/* ── Footer links ────────────────── */}
        <div className="mt-4 text-center text-[10px] text-[#444] space-x-3">
          <Link to="/research" className="hover:text-[#a3a3a3] no-underline">Research</Link>
          <Link to="/trade-calculator" className="hover:text-[#a3a3a3] no-underline">Calculator</Link>
          <Link to="/research/do-teams-that-trade-more-win" className="hover:text-[#a3a3a3] no-underline">Trade Volume</Link>
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
        </div>
      </article>
    </div>
  );
}
